import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { EmptyCon, Scrollable } from 'components/atoms';
import QUERY_KEYS from 'network/config/queryKeys';
import {
  getCancelledOrders,
  getCompletedOrders,
  getOngoingOrders,
  getOrderHistories,
  getRecurringOrders
} from 'network/services/order';
import React from 'react';
import { useInfiniteQuery } from 'react-query';
import { OrderCard } from '../OrderCard/OrderCard';

interface Props {
  noneMessage?: string;
  variant: 'ONGOING' | 'COMPLETE' | 'CANCELED' | 'ALL' | 'RECURRING';
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  width: 100%;
  padding: 0 4px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding: 0 0px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    padding: 0 1px;
  }

  @media (min-width: 1280px) {
    gap: 20px;
    padding: 0 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 20px;
`;

const LoadMoreIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  margin-top: 8px;
  grid-column: 1 / -1; /* Span across all columns */
`;

const LIMIT = 20;

const OrderList: React.FC<Props> = ({ noneMessage, variant }) => {
  const { data, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      variant === 'RECURRING'
        ? QUERY_KEYS.RECURRING_ORDERS
        : variant === 'ONGOING'
        ? QUERY_KEYS.ONGOING_ORDERS
        : variant === 'COMPLETE'
        ? QUERY_KEYS.COMPLETE_ORDERS
        : variant === 'CANCELED'
        ? QUERY_KEYS.RECURRING_ORDERS
        : QUERY_KEYS.ALL_ORDERS
    ],
    queryFn: ({ pageParam = 0 }) =>
      variant === 'RECURRING'
        ? getRecurringOrders({ size: LIMIT, page: pageParam })
        : variant === 'ONGOING'
        ? getOngoingOrders({ size: LIMIT, page: pageParam })
        : variant === 'COMPLETE'
        ? getCompletedOrders({ size: LIMIT, page: pageParam })
        : variant === 'CANCELED'
        ? getCancelledOrders({ size: LIMIT, page: pageParam })
        : getOrderHistories({ size: LIMIT, page: pageParam }),
    getNextPageParam: (lastPage, allPages) =>
      Array.isArray(lastPage) && lastPage.length === LIMIT ? allPages.length : undefined
  });

  const orders = (data?.pages ?? []).flat();

  const fetchMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      {isFetching && orders.length === 0 ? (
        <LoadingContainer>
          <CircularProgress size={30} />
        </LoadingContainer>
      ) : (
        <Scrollable onScrollBottom={fetchMore}>
          <GridContainer>
            {orders.length > 0 &&
              orders.map(order => {
                const item = order as unknown as React.ComponentProps<typeof OrderCard>['item'];
                return <OrderCard item={item} key={order.id} />;
              })}

            {/* Loading indicator inside the grid */}
            {isFetchingNextPage && (
              <LoadMoreIndicator>
                <CircularProgress size={20} />
              </LoadMoreIndicator>
            )}
          </GridContainer>
        </Scrollable>
      )}

      {orders.length < 1 && !(isFetching || isFetchingNextPage) && (
        <EmptyCon>{noneMessage ?? 'No ongoing order items'}</EmptyCon>
      )}
    </>
  );
};

export default OrderList;
